import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInbox,
  faCheckCircle,
  faExclamationCircle,
  faArrowRight,
  faPaperPlane,
  faHeadset,
} from '@fortawesome/free-solid-svg-icons';
import { setCredentials } from '../store/slices/authSlice';
import { authAPI } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import AuthLangSwitcher from '../components/AuthLangSwitcher';
import { openMailClient } from '../utils/mailSupport';
import '../styles/AuthModern.css';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useI18n();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailFromState = (location.state as any)?.email || '';

  const [status, setStatus] = useState<'waiting' | 'verifying' | 'success' | 'error'>(
    token ? 'verifying' : 'waiting'
  );
  const [message, setMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendEmail, setResendEmail] = useState(emailFromState);

  useEffect(() => {
    if (!token) return;

    const verifyEmail = async () => {
      try {
        const response = await authAPI.verifyEmail(token);
        dispatch(setCredentials({ user: response.data.user, token: response.data.token }));
        setStatus('success');
        setMessage(t('auth.verifiedSuccess'));
        setTimeout(() => navigate('/'), 2500);
      } catch (err: any) {
        setStatus('error');
        setMessage(err.response?.data?.error || t('auth.verificationFailed'));
      }
    };

    verifyEmail();
  }, [token, navigate, dispatch, t]);

  const handleResend = async () => {
    if (!resendEmail) return;
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await authAPI.resendVerification(resendEmail);
      setResendSuccess(true);
    } catch (err: any) {
      setMessage(err.response?.data?.error || t('auth.resendFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-status-layout">
        <div className="auth-status-card">
          <AuthLangSwitcher />

          {/* WAITING — user just signed up */}
          {status === 'waiting' && (
            <>
              <div className="auth-status-icon info">
                <FontAwesomeIcon icon={faInbox} />
              </div>
              <h2 className="auth-status-title">{t('auth.checkYourInbox')}</h2>
              <p className="auth-status-desc">{t('auth.weSentVerification')}</p>
              {emailFromState && (
                <p className="auth-status-email">{emailFromState}</p>
              )}
              <p className="auth-status-hint">{t('auth.checkSpam')}</p>

              <div className="auth-status-actions">
                {resendSuccess ? (
                  <div className="auth-success" style={{ maxWidth: 320, width: '100%' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>{t('auth.resendSuccess')}</span>
                  </div>
                ) : (
                  <>
                    {!emailFromState && (
                      <input
                        type="email"
                        className="auth-resend-input"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        placeholder={t('auth.enterEmailResend')}
                      />
                    )}
                    <button
                      className="auth-submit-btn"
                      onClick={handleResend}
                      disabled={resendLoading || !resendEmail}
                      style={{ maxWidth: 320 }}
                    >
                      {resendLoading ? (
                        <>
                          <span className="auth-spinner" />
                          {t('auth.sending')}
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPaperPlane} />
                          {t('auth.resendVerification')}
                        </>
                      )}
                    </button>
                  </>
                )}

                <button className="auth-ghost-btn" onClick={() => navigate('/login')}>
                  {t('auth.alreadyVerified')}
                </button>
              </div>
            </>
          )}

          {/* VERIFYING — token found, checking */}
          {status === 'verifying' && (
            <>
              <div className="auth-status-icon loading">
                <span className="auth-spinner" style={{ width: 32, height: 32 }} />
              </div>
              <h2 className="auth-status-title">{t('auth.verifying')}</h2>
              <p className="auth-status-desc">{t('auth.verifyingDesc')}</p>
            </>
          )}

          {/* SUCCESS */}
          {status === 'success' && (
            <>
              <div className="auth-status-icon success">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <h2 className="auth-status-title success">{t('auth.emailVerified')}</h2>
              <p className="auth-status-desc">{message}</p>
              <div className="auth-status-actions">
                <button
                  className="auth-submit-btn"
                  onClick={() => navigate('/')}
                  style={{ maxWidth: 320 }}
                >
                  {t('auth.goToDashboard')}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            </>
          )}

          {/* ERROR */}
          {status === 'error' && (
            <>
              <div className="auth-status-icon error">
                <FontAwesomeIcon icon={faExclamationCircle} />
              </div>
              <h2 className="auth-status-title error">{t('auth.verificationFailed')}</h2>
              <p className="auth-status-desc">{message}</p>

              <div className="auth-status-actions">
                <input
                  type="email"
                  className="auth-resend-input"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder={t('auth.enterEmailResend')}
                />
                {resendSuccess ? (
                  <div className="auth-success" style={{ maxWidth: 320, width: '100%' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>{t('auth.verificationSent')}</span>
                  </div>
                ) : (
                  <button
                    className="auth-submit-btn"
                    onClick={handleResend}
                    disabled={resendLoading || !resendEmail}
                    style={{ maxWidth: 320 }}
                  >
                    {resendLoading ? (
                      <>
                        <span className="auth-spinner" />
                        {t('auth.sending')}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        {t('auth.resendVerification')}
                      </>
                    )}
                  </button>
                )}
                <button className="auth-ghost-btn" onClick={() => navigate('/login')}>
                  {t('auth.goToLogin')}
                </button>
              </div>

              <div className="auth-support-section">
                <p className="auth-support-text">{t('auth.havingProblems')}</p>
                <button
                  type="button"
                  className="auth-support-btn"
                  onClick={() => openMailClient(t('auth.supportSubject'), t('auth.supportBody'))}
                >
                  <FontAwesomeIcon icon={faHeadset} />
                  {t('auth.reachOutToUs')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
